import type { RequestFn } from '../http';

export interface PortalTimeTechnician {
  id: string;
  name: string;
}

export interface PortalTimeTechniciansResponse {
  mode: 'CW_ONLY' | 'WECLAPP_ONLY';
  technicians: PortalTimeTechnician[];
}

export type PortalTimeBillingType = 'billable' | 'internal' | 'goodwill' | 'non-billable';

export const PORTAL_TIME_BILLING_LABELS: Record<PortalTimeBillingType, string> = {
  billable: 'Abrechenbar',
  internal: 'Intern',
  goodwill: 'Kulanz',
  'non-billable': 'Nicht abrechenbar',
};

export interface PortalTimeBookingPayload {
  weclappCustomerId: string;
  summary: string;
  description?: string;
  minutes: number;
  workType?: string;
  technicianId?: string;
  billingType?: PortalTimeBillingType;
  billingPercent?: number;
  billingReason?: string;
  /**
   * Woraus die Zeit bezahlt wird — Regelbetrieb (Standard), Angebot oder Projekt.
   * Andere Frage als `billingType` (ob abgerechnet wird). Nur der Regelbetrieb zählt
   * gegen das Betreuungskontingent des Kunden.
   */
  billingScope?: 'STANDARD' | 'QUOTE' | 'PROJECT';
  /** Angebots- bzw. Projektnummer — Pflicht, sobald `billingScope` nicht STANDARD ist */
  billingScopeReference?: string;
  /** Leistungsart (Weclapp-Artikel-ID aus den konfigurierten Optionen); fehlt: Standard */
  articleId?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  breakMinutes?: number;
}

export interface PortalTimeBookingWarningDetail {
  step: 'comment' | 'timeEntry' | 'close';
  label: string;
  /** Lesbare Ursache */
  reason: string;
  /** Technischer Originaltext, falls abweichend */
  detail?: string;
}

export interface PortalTimeBookingSuccess {
  success: true;
  portalCorrelationId: string;
  cwTicketId?: number;
  weclappTicketId?: string;
  weclappTicketNumber?: string;
  ticketSyncMapId: string;
  /** Nicht-fatale Probleme (z. B. Schließen fehlgeschlagen) — werden serverseitig automatisch nachgeholt */
  warnings?: string[];
  /** Ursache je fehlgeschlagenem Schritt */
  warningDetails?: PortalTimeBookingWarningDetail[];
}

/**
 * Ein Techniker hat einen Kollegen gewählt (v1.404.0): nichts gebucht, ein Zeitvorschlag wartet auf
 * dessen Freigabe (HTTP 202).
 */
export interface TimeProposalCreatedResponse {
  success: true;
  proposed: true;
  duplicate?: boolean;
  proposal: { id: string; recipientName: string };
  message: string;
}

export interface PortalTimeBookingFailBody {
  success: false;
  error: string;
  cwTicketId?: number;
  portalCorrelationId?: string;
  ticketSyncMapId?: string;
  /** true: Buchung ist gespeichert, der Server versucht es automatisch erneut */
  retryScheduled?: boolean;
}

export interface PortalTimeBookingPeriodStats {
  from: string;
  to: string;
  bookings: number;
  totalMinutes: number;
  billableMinutes: number;
  byType: Record<PortalTimeBillingType, number>;
}

export interface BookingRoundingStats {
  bookings: number;
  /** Minutengenau erfasst */
  actualMinutes: number;
  /** Nach Weclapp gebucht (auf 15 Minuten aufgerundet) */
  bookedMinutes: number;
}

export interface PortalTimeBookingStatsResponse {
  technicianId: string | null;
  current: PortalTimeBookingPeriodStats;
  previous: PortalTimeBookingPeriodStats;
  /** Erfasst vs. gebucht über alle Buchungswege (Cockpit, Zeitbuchung, Berichte) */
  rounding?: { current: BookingRoundingStats; previous: BookingRoundingStats };
}

/**
 * Klartext-Diagnose zu einer gescheiterten Buchung (Backend:
 * services/timeBookingDiagnosis.ts): Ursache, was das Portal automatisch dagegen tut
 * und was ein Mensch tun muss.
 */
export interface BookingDiagnosis {
  code: string;
  title: string;
  cause: string;
  /** Wer die Ursache beheben kann: das Portal selbst, ein Admin, jemand in Weclapp — oder unklar. */
  owner: 'PORTAL' | 'ADMIN' | 'WECLAPP' | 'UNBEKANNT';
  /** Was das Portal automatisch unternimmt (null = nichts automatisch möglich). */
  autoFix: string | null;
  manualSteps: string[];
  retryWorthwhile: boolean;
  /** Die Klartext-Sätze aus Weclapps JSON-Antwort. */
  weclappMessages: string[];
}

export interface AdminPortalTimeBooking {
  id: string;
  createdAt: string;
  bookingDate: string | null;
  summary: string | null;
  customerName: string | null;
  weclappCustomerId: string;
  technicianName: string | null;
  createdByName: string | null;
  minutes: number;
  billingType: string;
  mode: string;
  status: 'PENDING' | 'SYNCED' | 'ERROR';
  attempts: number;
  lastError: string | null;
  diagnosis: BookingDiagnosis | null;
  weclappTicketId: string | null;
  weclappTicketNumber: string | null;
  /** Deep-Link in die Weclapp-Ticketansicht (null, wenn keine Instanz konfiguriert ist). */
  weclappUrl: string | null;
  cwTicketId: number | null;
  pendingSteps: { ticket: boolean; comment: boolean; timeEntry: boolean; close: boolean };
}

export interface AdminPortalTimeBookingRetryResult {
  status: 'SYNCED' | 'PENDING' | 'ERROR' | 'SKIPPED';
  warnings: string[];
}

/** Geparkte Ticket-Zeitbuchung aus dem Techniker-Cockpit (Warteliste). */
export interface AdminWeclappWriteLogEntry {
  id: string;
  operationType: 'TICKET_COMMENT' | 'TIME_RECORD';
  createdAt: string;
  updatedAt: string;
  status: 'FAILED' | 'UNKNOWN' | 'ACKNOWLEDGED' | 'SUCCESS' | 'PENDING';
  errorCode: string | null;
  /** Echte Weclapp-Antwort — Einträge vor v1.295.1 haben nur den Code. */
  errorMessage: string | null;
  /** Gesendeter Inhalt: Kommentar `{comment, htmlComment, internal}` bzw. Buchung `{hours, startDate, comment, …}`. */
  payload: Record<string, unknown> | null;
  weclappTicketId: string;
  /** Deep-Link in die Weclapp-Ticketansicht (null, wenn keine Instanz konfiguriert ist). */
  weclappUrl: string | null;
  ticketNumber: string | null;
  ticketSubject: string | null;
  partyName: string | null;
  userName: string | null;
  lastVerify: {
    at: string;
    outcome: 'FOUND' | 'NOT_FOUND' | 'AMBIGUOUS';
    method?: 'text' | 'author_time';
    commentId?: string;
  } | null;
}

export interface AdminWeclappWriteLogVerifyResult {
  scanned: number;
  found: number;
  notFound: number;
  ambiguous: number;
  errors: number;
}

export interface AdminWeclappApiErrorEntry {
  id: string;
  createdAt: string;
  method: string;
  endpoint: string;
  status: number;
  message: string;
  tokenKind: 'personal' | 'global';
  userName: string | null;
  /** Auslöser: Job-Name, 'ui' (ein Mensch wartete) oder null (Altbestand). */
  source: string | null;
  ticketId: string | null;
  ticketNumber: string | null;
  ticketSubject: string | null;
}

/** Gleiche Meldung, gleicher Vorgang — zusammengefasst statt 200-mal untereinander. */
export interface AdminWeclappApiErrorGroup {
  key: string;
  method: string;
  endpoint: string;
  status: number;
  message: string;
  ticketId: string | null;
  ticketNumber: string | null;
  ticketSubject: string | null;
  sources: string[];
  userNames: string[];
  count: number;
  firstAt: string;
  lastAt: string;
  entryIds: string[];
}

export interface AdminWeclappApiErrorsResponse {
  retentionDays: number;
  entries: AdminWeclappApiErrorEntry[];
  groups: AdminWeclappApiErrorGroup[];
}

export interface AdminTicketTimeBooking {
  id: string;
  createdAt: string;
  startDate: string | null;
  weclappTicketId: string;
  /** Deep-Link in die Weclapp-Ticketansicht (null, wenn keine Instanz konfiguriert ist). */
  weclappUrl: string | null;
  ticketNumber: string | null;
  ticketSubject: string | null;
  technicianName: string | null;
  createdByName: string | null;
  hours: number;
  comment: string;
  status: 'PENDING' | 'SYNCED' | 'ERROR';
  attempts: number;
  lastError: string | null;
  diagnosis: BookingDiagnosis | null;
  pendingSteps: { timeEntry: boolean; commentInternal: boolean; commentPublic: boolean };
}

/** Ein Lauf einer geparkten Buchung — Erst-Request oder Retry — mit Weclapps Antwort. */
export interface AdminBookingAttempt {
  id: string;
  attempt: number;
  outcome: 'REJECTED' | 'SYNCED' | 'PENDING' | 'ERROR';
  error: string | null;
  /** Diagnose zu genau diesem Versuch — zeigt, ob sich die Ursache über die Läufe geändert hat. */
  diagnosis: BookingDiagnosis | null;
  warnings: string[];
  createdAt: string;
}

export interface AdminTicketTimeBookingRetryAllResult {
  scanned: number;
  synced: number;
  pending: number;
  failed: number;
}

/**
 * Ein wartender Weclapp-Schreibvorgang aus dem Notbetrieb: eine Ticketfeld-Änderung oder
 * ein Kommentar, der geschrieben wurde, während Weclapp nicht erreichbar war.
 */
/**
 * `TICKET`: ein im ServiceHub angelegtes Ticket, das weclapp noch nicht angenommen hat
 * (Nachsync, Admin → Sync). `id` ist dann die interne ID `local-…`.
 * `DOCUMENT`: ein Anhang, der im ServiceHub am Ticket hängt, aber noch nicht in weclapp.
 */
export type AdminPendingWorkKind = 'EDIT' | 'COMMENT' | 'TICKET' | 'DOCUMENT';

const PENDING_WORK_PATH: Record<AdminPendingWorkKind, string> = {
  EDIT: 'edit',
  COMMENT: 'comment',
  TICKET: 'ticket',
  DOCUMENT: 'document',
};

export interface AdminPendingWorkItem {
  id: string;
  kind: AdminPendingWorkKind;
  ticketId: string;
  ticketNumber: string | null;
  ticketSubject: string | null;
  partyName: string | null;
  weclappUrl: string | null;
  status: 'PENDING' | 'SYNCED' | 'FAILED';
  /** Was wartet: geänderte Felder bzw. der Kommentaranfang. */
  summary: string;
  authorName: string | null;
  /** Kommentare: wer geschrieben hat. Tickets: der Anlageweg (CHAT, CUSTOMER_REQUEST …). */
  source: string | null;
  attempts: number;
  lastError: string | null;
  /** Klartext-Sätze aus Weclapps Antwort — ohne sie steht dort nur „validation failed". */
  weclappMessages: string[];
  /** Abbruch ohne Antwort: der Kommentar kann bereits in Weclapp stehen. */
  outcomeUnknown: boolean;
  /** Darf „nochmal senden" angeboten werden? */
  replaySafe: boolean;
  /**
   * Abgelehnt, aber der Status steht im ServiceHub (dem Kunden schon gemeldet). Abhaken gibt
   * ihn frei: weclapps Stand kommt zurück, verknüpfte Chats bekommen ihn per WhatsApp.
   */
  holdsStatus?: boolean;
  createdAt: string;
  updatedAt: string;
  syncedAt: string | null;
  acknowledgedAt: string | null;
}

export interface PendingWorkKindCount {
  waiting: number;
  stuck: number;
}

export interface PendingWorkTicket {
  ticketId: string;
  ticketNumber: string | null;
  subject: string | null;
  waiting: number;
  stuck: number;
}

export interface PendingWorkCounters {
  /** Liegt im Portal und wird automatisch nachgeschickt. */
  waiting: number;
  /** Endgültig gescheitert — kein Job versucht es erneut. */
  stuck: number;
  oldestWaitingAt: string | null;
  byKind: {
    EDIT: PendingWorkKindCount;
    COMMENT: PendingWorkKindCount;
    TIME_BOOKING: PendingWorkKindCount;
    /** Im ServiceHub angelegte Tickets, die weclapp noch nicht hat (ältere Server: fehlt) */
    TICKET?: PendingWorkKindCount;
    /** Anhänge, die weclapp noch nicht hat (ältere Server: fehlt) */
    DOCUMENT?: PendingWorkKindCount;
  };
  /** Betroffene Tickets, hängengebliebene zuerst — begrenzt auf die wichtigsten. */
  tickets: PendingWorkTicket[];
}

export interface AdminPendingWorkResponse {
  items: AdminPendingWorkItem[];
  counters: PendingWorkCounters;
}

export interface AdminPendingWorkRetryResult {
  status: 'PENDING' | 'SYNCED' | 'FAILED' | 'UNKNOWN';
  stats: { scanned: number; synced: number; pending: number; failed: number };
}

export function createPortalTimeBookingApi(request: RequestFn) {
  return {
    async getPortalTimeTechnicians(): Promise<PortalTimeTechniciansResponse> {
      return request<PortalTimeTechniciansResponse>('/portal/time-bookings/technicians');
    },
    async submitPortalTimeBooking(
      data: PortalTimeBookingPayload
    ): Promise<PortalTimeBookingSuccess | TimeProposalCreatedResponse> {
      return request<PortalTimeBookingSuccess | TimeProposalCreatedResponse>('/portal/time-bookings', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    async getPortalTimeBookingStats(technicianId?: string): Promise<PortalTimeBookingStatsResponse> {
      const qs = technicianId ? `?technicianId=${encodeURIComponent(technicianId)}` : '';
      return request<PortalTimeBookingStatsResponse>(`/portal/time-bookings/stats${qs}`);
    },
    async getAdminPortalTimeBookings(status?: string): Promise<{ bookings: AdminPortalTimeBooking[] }> {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return request<{ bookings: AdminPortalTimeBooking[] }>(`/admin/time-bookings${qs}`);
    },
    async retryAdminPortalTimeBooking(id: string): Promise<AdminPortalTimeBookingRetryResult> {
      return request<AdminPortalTimeBookingRetryResult>(
        `/admin/time-bookings/${encodeURIComponent(id)}/retry`,
        { method: 'POST' }
      );
    },
    async retryAllAdminPortalTimeBookings(): Promise<AdminTicketTimeBookingRetryAllResult> {
      return request<AdminTicketTimeBookingRetryAllResult>('/admin/time-bookings/retry-all', {
        method: 'POST',
      });
    },
    async getAdminTicketTimeBookings(status?: string): Promise<{ bookings: AdminTicketTimeBooking[] }> {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return request<{ bookings: AdminTicketTimeBooking[] }>(`/admin/ticket-time-bookings${qs}`);
    },
    async getAdminTicketTimeBookingAttempts(id: string): Promise<{ attempts: AdminBookingAttempt[] }> {
      return request<{ attempts: AdminBookingAttempt[] }>(`/admin/ticket-time-bookings/${encodeURIComponent(id)}/attempts`);
    },
    async getAdminPortalTimeBookingAttempts(id: string): Promise<{ attempts: AdminBookingAttempt[] }> {
      return request<{ attempts: AdminBookingAttempt[] }>(`/admin/time-bookings/${encodeURIComponent(id)}/attempts`);
    },
    async retryAdminTicketTimeBooking(id: string): Promise<AdminPortalTimeBookingRetryResult> {
      return request<AdminPortalTimeBookingRetryResult>(
        `/admin/ticket-time-bookings/${encodeURIComponent(id)}/retry`,
        { method: 'POST' }
      );
    },
    async getAdminWeclappWriteLog(status?: string): Promise<{ entries: AdminWeclappWriteLogEntry[] }> {
      const qs = status ? `?type=TICKET_COMMENT&status=${encodeURIComponent(status)}` : '?type=TICKET_COMMENT';
      return request<{ entries: AdminWeclappWriteLogEntry[] }>(`/admin/weclapp-write-log${qs}`);
    },
    async getAdminWeclappApiErrors(search?: string): Promise<AdminWeclappApiErrorsResponse> {
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      return request<AdminWeclappApiErrorsResponse>(`/admin/weclapp-api-errors${qs}`);
    },
    async acknowledgeAdminWeclappWriteLog(id: string): Promise<{ success: true }> {
      return request<{ success: true }>(`/admin/weclapp-write-log/${encodeURIComponent(id)}/acknowledge`, {
        method: 'POST',
      });
    },
    async verifyAdminWeclappWriteLog(): Promise<AdminWeclappWriteLogVerifyResult> {
      return request<AdminWeclappWriteLogVerifyResult>('/admin/weclapp-write-log/verify', {
        method: 'POST',
      });
    },
    async retryAllAdminTicketTimeBookings(): Promise<AdminTicketTimeBookingRetryAllResult> {
      return request<AdminTicketTimeBookingRetryAllResult>(
        '/admin/ticket-time-bookings/retry-all',
        { method: 'POST' }
      );
    },
    async getAdminPendingWork(
      scope: 'waiting' | 'stuck' | 'done' = 'waiting'
    ): Promise<AdminPendingWorkResponse> {
      return request<AdminPendingWorkResponse>(`/admin/weclapp-pending-work?scope=${scope}`);
    },
    /**
     * Zurück in die Warteschlange und sofort ein Versuch.
     *
     * `confirmDuplicateRisk` ist nur für Kommentare mit unklarem Ausgang nötig; ohne die
     * Bestätigung antwortet der Server mit 409 und erklärt, warum.
     */
    async retryAdminPendingWork(
      kind: AdminPendingWorkKind,
      id: string,
      confirmDuplicateRisk = false
    ): Promise<AdminPendingWorkRetryResult> {
      return request<AdminPendingWorkRetryResult>(
        `/admin/weclapp-pending-work/${PENDING_WORK_PATH[kind]}/${encodeURIComponent(id)}/retry`,
        { method: 'POST', body: JSON.stringify({ confirmDuplicateRisk }) }
      );
    },
    async acknowledgeAdminPendingWork(
      kind: AdminPendingWorkKind,
      id: string
    ): Promise<{ ok: true }> {
      return request<{ ok: true }>(
        `/admin/weclapp-pending-work/${PENDING_WORK_PATH[kind]}/${encodeURIComponent(id)}/acknowledge`,
        { method: 'POST', body: JSON.stringify({}) }
      );
    },
    /**
     * Ein im ServiceHub angelegtes Ticket von Hand mit einem weclapp-Ticket verbinden (Nummer
     * T26-… oder weclapp-ID). Danach gilt die weclapp-ID.
     */
    async linkAdminLocalTicket(
      id: string,
      ref: string
    ): Promise<{ ok: true; weclappTicketId: string; ticketNumber: string }> {
      return request(`/admin/weclapp-pending-work/ticket/${encodeURIComponent(id)}/link`, {
        method: 'POST',
        body: JSON.stringify({ ref }),
      });
    },
  };
}
